"use strict";
/*
This file is part of web3.js.

web3.js is free software: you can redistribute it and/or modify
it under the terms of the GNU Lesser General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

web3.js is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Lesser General Public License for more details.

You should have received a copy of the GNU Lesser General Public License
along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.Interface = exports.Indexed = exports.ErrorDescription = exports.TransactionDescription = exports.LogDescription = exports.checkResultErrors = void 0;
const address_1 = require("@ethersproject/address");
const bignumber_1 = require("@ethersproject/bignumber");
const bytes_1 = require("@ethersproject/bytes");
const hash_1 = require("@ethersproject/hash");
const keccak256_1 = require("@ethersproject/keccak256");
const properties_1 = require("@ethersproject/properties");
const logger_1 = require("@ethersproject/logger");
const abi_coder_js_1 = require("./abi-coder.js");
const abstract_coder_js_1 = require("./coders/abstract-coder.js");
Object.defineProperty(exports, "checkResultErrors", { enumerable: true, get: function () { return abstract_coder_js_1.checkResultErrors; } });
const fragments_js_1 = require("./fragments.js");
const _version_js_1 = require("./_version.js");
const logger = new logger_1.Logger(_version_js_1.version);
class LogDescription extends properties_1.Description {
    eventFragment;
    name;
    signature;
    topic;
    args;
}
exports.LogDescription = LogDescription;
class TransactionDescription extends properties_1.Description {
    functionFragment;
    name;
    args;
    signature;
    sighash;
    value;
}
exports.TransactionDescription = TransactionDescription;
class ErrorDescription extends properties_1.Description {
    errorFragment;
    name;
    args;
    signature;
    sighash;
}
exports.ErrorDescription = ErrorDescription;
class Indexed extends properties_1.Description {
    hash;
    _isIndexed;
    static isIndexed(value) {
        return !!(value && value._isIndexed);
    }
}
exports.Indexed = Indexed;
const BuiltinErrors = {
    '0x08c379a0': { signature: 'Error(string)', name: 'Error', inputs: ['string'], reason: true },
    '0x4e487b71': { signature: 'Panic(uint256)', name: 'Panic', inputs: ['uint256'] },
};
function wrapAccessError(property, error) {
    const wrap = new Error(`deferred error during ABI decoding triggered accessing ${property}`);
    wrap.error = error;
    return wrap;
}
/*
function checkNames(fragment: Fragment, type: "input" | "output", params: Array<ParamType>): void {
    params.reduce((accum, param) => {
        if (param.name) {
            if (accum[param.name]) {
                logger.throwArgumentError(`duplicate ${ type } parameter ${ JSON.stringify(param.name) } in ${ fragment.format("full") }`, "fragment", fragment);
            }
            accum[param.name] = true;
        }
        return accum;
    }, <{ [ name: string ]: boolean }>{ });
}
*/
class Interface {
    fragments;
    errors;
    events;
    functions;
    structs;
    deploy;
    _abiCoder;
    _isInterface;
    constructor(fragments) {
        let abi = [];
        if (typeof fragments === 'string') {
            abi = JSON.parse(fragments);
        }
        else {
            abi = fragments;
        }
        (0, properties_1.defineReadOnly)(this, 'fragments', abi.map(fragment => fragments_js_1.Fragment.from(fragment)).filter(fragment => fragment != null));
        (0, properties_1.defineReadOnly)(this, '_abiCoder', (0, properties_1.getStatic)(new.target, 'getAbiCoder')());
        (0, properties_1.defineReadOnly)(this, 'functions', {});
        (0, properties_1.defineReadOnly)(this, 'errors', {});
        (0, properties_1.defineReadOnly)(this, 'events', {});
        (0, properties_1.defineReadOnly)(this, 'structs', {});
        // Add all fragments by their signature
        this.fragments.forEach(fragment => {
            let bucket = null;
            switch (fragment.type) {
                case 'constructor':
                    if (this.deploy) {
                        logger.warn('duplicate definition - constructor');
                        return;
                    }
                    // checkNames(fragment, "input", fragment.inputs);
                    (0, properties_1.defineReadOnly)(this, 'deploy', fragment);
                    return;
                case 'function':
                    // checkNames(fragment, "input", fragment.inputs);
                    // checkNames(fragment, "output", (<FunctionFragment>fragment).outputs);
                    bucket = this.functions;
                    break;
                case 'event':
                    // checkNames(fragment, "input", fragment.inputs);
                    bucket = this.events;
                    break;
                case 'error':
                    bucket = this.errors;
                    break;
                default:
                    return;
            }
            const signature = fragment.format();
            if (bucket[signature]) {
                logger.warn(`duplicate definition - ${signature}`);
                return;
            }
            bucket[signature] = fragment;
        });
        // If we do not have a constructor add a default
        if (!this.deploy) {
            (0, properties_1.defineReadOnly)(this, 'deploy', fragments_js_1.ConstructorFragment.from({
                payable: false,
                type: 'constructor',
            }));
        }
        (0, properties_1.defineReadOnly)(this, '_isInterface', true);
    }
    format(format) {
        if (!format) {
            format = fragments_js_1.FormatTypes.full;
        }
        if (format === fragments_js_1.FormatTypes.sighash) {
            logger.throwArgumentError('interface does not support formatting sighash', 'format', format);
        }
        const abi = this.fragments.map(fragment => fragment.format(format));
        // We need to re-bundle the JSON fragments a bit
        if (format === fragments_js_1.FormatTypes.json) {
            return JSON.stringify(abi.map(j => JSON.parse(j)));
        }
        return abi;
    }
    // Sub-classes can override these to handle other blockchains
    static getAbiCoder() {
        return abi_coder_js_1.defaultAbiCoder;
    }
    static getAddress(address) {
        return (0, address_1.getAddress)(address);
    }
    static getSighash(fragment) {
        return (0, bytes_1.hexDataSlice)((0, hash_1.id)(fragment.format()), 0, 4);
    }
    static getEventTopic(eventFragment) {
        return (0, hash_1.id)(eventFragment.format());
    }
    // Find a function definition by any means necessary (unless it is ambiguous)
    getFunction(nameOrSignatureOrSighash) {
        if ((0, bytes_1.isHexString)(nameOrSignatureOrSighash)) {
            for (const name in this.functions) {
                if (nameOrSignatureOrSighash === this.getSighash(name)) {
                    return this.functions[name];
                }
            }
            logger.throwArgumentError('no matching function', 'sighash', nameOrSignatureOrSighash);
        }
        // It is a bare name, look up the function (will return null if ambiguous)
        if (!nameOrSignatureOrSighash.includes('(')) {
            const name = nameOrSignatureOrSighash.trim();
            const matching = Object.keys(this.functions).filter(f => f.split('(' /* fix:) */)[0] === name);
            if (matching.length === 0) {
                logger.throwArgumentError('no matching function', 'name', name);
            }
            else if (matching.length > 1) {
                logger.throwArgumentError('multiple matching functions', 'name', name);
            }
            return this.functions[matching[0]];
        }
        // Normalize the signature and lookup the function
        const result = this.functions[fragments_js_1.FunctionFragment.fromString(nameOrSignatureOrSighash).format()];
        if (!result) {
            logger.throwArgumentError('no matching function', 'signature', nameOrSignatureOrSighash);
        }
        return result;
    }
    // Find an event definition by any means necessary (unless it is ambiguous)
    getEvent(nameOrSignatureOrTopic) {
        if ((0, bytes_1.isHexString)(nameOrSignatureOrTopic)) {
            const topichash = nameOrSignatureOrTopic.toLowerCase();
            for (const name in this.events) {
                if (topichash === this.getEventTopic(name)) {
                    return this.events[name];
                }
            }
            logger.throwArgumentError('no matching event', 'topichash', topichash);
        }
        // It is a bare name, look up the function (will return null if ambiguous)
        if (!nameOrSignatureOrTopic.includes('(')) {
            const name = nameOrSignatureOrTopic.trim();
            const matching = Object.keys(this.events).filter(f => f.split('(' /* fix:) */)[0] === name);
            if (matching.length === 0) {
                logger.throwArgumentError('no matching event', 'name', name);
            }
            else if (matching.length > 1) {
                logger.throwArgumentError('multiple matching events', 'name', name);
            }
            return this.events[matching[0]];
        }
        // Normalize the signature and lookup the function
        const result = this.events[fragments_js_1.EventFragment.fromString(nameOrSignatureOrTopic).format()];
        if (!result) {
            logger.throwArgumentError('no matching event', 'signature', nameOrSignatureOrTopic);
        }
        return result;
    }
    // Find a function definition by any means necessary (unless it is ambiguous)
    getError(nameOrSignatureOrSighash) {
        if ((0, bytes_1.isHexString)(nameOrSignatureOrSighash)) {
            const getSighash = (0, properties_1.getStatic)(this.constructor, 'getSighash');
            for (const name in this.errors) {
                const error = this.errors[name];
                if (nameOrSignatureOrSighash === getSighash(error)) {
                    return this.errors[name];
                }
            }
            logger.throwArgumentError('no matching error', 'sighash', nameOrSignatureOrSighash);
        }
        // It is a bare name, look up the function (will return null if ambiguous)
        if (!nameOrSignatureOrSighash.includes('(')) {
            const name = nameOrSignatureOrSighash.trim();
            const matching = Object.keys(this.errors).filter(f => f.split('(' /* fix:) */)[0] === name);
            if (matching.length === 0) {
                logger.throwArgumentError('no matching error', 'name', name);
            }
            else if (matching.length > 1) {
                logger.throwArgumentError('multiple matching errors', 'name', name);
            }
            return this.errors[matching[0]];
        }
        // Normalize the signature and lookup the function
        const result = this.errors[fragments_js_1.FunctionFragment.fromString(nameOrSignatureOrSighash).format()];
        if (!result) {
            logger.throwArgumentError('no matching error', 'signature', nameOrSignatureOrSighash);
        }
        return result;
    }
    // Get the sighash (the bytes4 selector) used by Hyperion to identify a function
    getSighash(fragment) {
        if (typeof fragment === 'string') {
            try {
                fragment = this.getFunction(fragment);
            }
            catch (error) {
                try {
                    fragment = this.getError(fragment);
                }
                catch (_) {
                    throw error;
                }
            }
        }
        return (0, properties_1.getStatic)(this.constructor, 'getSighash')(fragment);
    }
    // Get the topic (the bytes32 hash) used by Hyperion to identify an event
    getEventTopic(eventFragment) {
        if (typeof eventFragment === 'string') {
            eventFragment = this.getEvent(eventFragment);
        }
        return (0, properties_1.getStatic)(this.constructor, 'getEventTopic')(eventFragment);
    }
    _decodeParams(params, data) {
        return this._abiCoder.decode(params, data);
    }
    _encodeParams(params, values) {
        return this._abiCoder.encode(params, values);
    }
    encodeDeploy(values) {
        return this._encodeParams(this.deploy.inputs, values || []);
    }
    decodeErrorResult(fragment, data) {
        if (typeof fragment === 'string') {
            fragment = this.getError(fragment);
        }
        const bytes = (0, bytes_1.arrayify)(data);
        if ((0, bytes_1.hexlify)(bytes.slice(0, 4)) !== this.getSighash(fragment)) {
            logger.throwArgumentError(`data signature does not match error ${fragment.name}.`, 'data', (0, bytes_1.hexlify)(bytes));
        }
        return this._decodeParams(fragment.inputs, bytes.slice(4));
    }
    encodeErrorResult(fragment, values) {
        if (typeof fragment === 'string') {
            fragment = this.getError(fragment);
        }
        return (0, bytes_1.hexlify)((0, bytes_1.concat)([this.getSighash(fragment), this._encodeParams(fragment.inputs, values || [])]));
    }
    // Decode the data for a function call (e.g. tx.data)
    decodeFunctionData(functionFragment, data) {
        if (typeof functionFragment === 'string') {
            functionFragment = this.getFunction(functionFragment);
        }
        const bytes = (0, bytes_1.arrayify)(data);
        if ((0, bytes_1.hexlify)(bytes.slice(0, 4)) !== this.getSighash(functionFragment)) {
            logger.throwArgumentError(`data signature does not match function ${functionFragment.name}.`, 'data', (0, bytes_1.hexlify)(bytes));
        }
        return this._decodeParams(functionFragment.inputs, bytes.slice(4));
    }
    // Encode the data for a function call (e.g. tx.data)
    encodeFunctionData(functionFragment, values) {
        if (typeof functionFragment === 'string') {
            functionFragment = this.getFunction(functionFragment);
        }
        return (0, bytes_1.hexlify)((0, bytes_1.concat)([
            this.getSighash(functionFragment),
            this._encodeParams(functionFragment.inputs, values || []),
        ]));
    }
    // Decode the result from a function call (e.g. from qrl_call)
    decodeFunctionResult(functionFragment, data) {
        if (typeof functionFragment === 'string') {
            functionFragment = this.getFunction(functionFragment);
        }
        const bytes = (0, bytes_1.arrayify)(data);
        let reason = null;
        let message = '';
        let errorArgs = null;
        let errorName = null;
        let errorSignature = null;
        switch (bytes.length % this._abiCoder._getWordSize()) {
            case 0:
                try {
                    return this._abiCoder.decode(functionFragment.outputs, bytes);
                }
                catch (error) { }
                break;
            case 4: {
                const selector = (0, bytes_1.hexlify)(bytes.slice(0, 4));
                const builtin = BuiltinErrors[selector];
                if (builtin) {
                    errorArgs = this._abiCoder.decode(builtin.inputs, bytes.slice(4));
                    errorName = builtin.name;
                    errorSignature = builtin.signature;
                    if (builtin.reason) {
                        reason = errorArgs[0];
                    }
                    if (errorName === 'Error') {
                        message = `; VM Exception while processing transaction: reverted with reason string ${JSON.stringify(errorArgs[0])}`;
                    }
                    else if (errorName === 'Panic') {
                        message = `; VM Exception while processing transaction: reverted with panic code ${errorArgs[0]}`;
                    }
                }
                else {
                    try {
                        const error = this.getError(selector);
                        errorArgs = this._abiCoder.decode(error.inputs, bytes.slice(4));
                        errorName = error.name;
                        errorSignature = error.format();
                    }
                    catch (error) { }
                }
                break;
            }
        }
        return logger.throwError(`call revert exception${message}`, logger_1.Logger.errors.CALL_EXCEPTION, {
            method: functionFragment.format(),
            data: (0, bytes_1.hexlify)(data),
            errorArgs,
            errorName,
            errorSignature,
            reason,
        });
    }
    // Encode the result for a function call (e.g. for qrl_call)
    encodeFunctionResult(functionFragment, values) {
        if (typeof functionFragment === 'string') {
            functionFragment = this.getFunction(functionFragment);
        }
        return (0, bytes_1.hexlify)(this._abiCoder.encode(functionFragment.outputs, values || []));
    }
    // Create the filter for the event with search criteria (e.g. for qrl_filterLog)
    encodeFilterTopics(eventFragment, values) {
        if (typeof eventFragment === 'string') {
            eventFragment = this.getEvent(eventFragment);
        }
        if (values.length > eventFragment.inputs.length) {
            logger.throwError(`too many arguments for ${eventFragment.format()}`, logger_1.Logger.errors.UNEXPECTED_ARGUMENT, {
                argument: 'values',
                value: values,
            });
        }
        const topics = [];
        if (!eventFragment.anonymous) {
            topics.push(this.getEventTopic(eventFragment));
        }
        const encodeTopic = (param, value) => {
            if (param.type === 'string') {
                return (0, hash_1.id)(value);
            }
            if (param.type === 'bytes') {
                return (0, keccak256_1.keccak256)((0, bytes_1.hexlify)(value));
            }
            if (param.type === 'bool' && typeof value === 'boolean') {
                value = value ? '0x01' : '0x00';
            }
            if (param.type.match(/^u?int/)) {
                value = bignumber_1.BigNumber.from(value).toHexString();
            }
            // Check addresses are valid
            if (param.type === 'address') {
                this._abiCoder.encode(['address'], [value]);
            }
            return (0, bytes_1.hexZeroPad)((0, bytes_1.hexlify)(value), 32);
        };
        values.forEach((value, index) => {
            const param = eventFragment.inputs[index];
            if (!param.indexed) {
                if (value != null) {
                    logger.throwArgumentError('cannot filter non-indexed parameters; must be null', `contract.${param.name}`, value);
                }
                return;
            }
            if (value == null) {
                topics.push(null);
            }
            else if (param.baseType === 'array' || param.baseType === 'tuple') {
                logger.throwArgumentError('filtering with tuples or arrays not supported', `contract.${param.name}`, value);
            }
            else if (Array.isArray(value)) {
                topics.push(value.map(value => encodeTopic(param, value)));
            }
            else {
                topics.push(encodeTopic(param, value));
            }
        });
        // Trim off trailing nulls
        while (topics.length && topics[topics.length - 1] === null) {
            topics.pop();
        }
        return topics;
    }
    encodeEventLog(eventFragment, values) {
        if (typeof eventFragment === 'string') {
            eventFragment = this.getEvent(eventFragment);
        }
        const topics = [];
        const dataTypes = [];
        const dataValues = [];
        if (!eventFragment.anonymous) {
            topics.push(this.getEventTopic(eventFragment));
        }
        if (values.length !== eventFragment.inputs.length) {
            logger.throwArgumentError('event arguments/values mismatch', 'values', values);
        }
        eventFragment.inputs.forEach((param, index) => {
            const value = values[index];
            if (param.indexed) {
                if (param.type === 'string') {
                    topics.push((0, hash_1.id)(value));
                }
                else if (param.type === 'bytes') {
                    topics.push((0, keccak256_1.keccak256)(value));
                }
                else if (param.baseType === 'tuple' || param.baseType === 'array') {
                    // @TODO
                    throw new Error('not implemented');
                }
                else {
                    topics.push(this._abiCoder.encode([param.type], [value]));
                }
            }
            else {
                dataTypes.push(param);
                dataValues.push(value);
            }
        });
        return {
            data: this._abiCoder.encode(dataTypes, dataValues),
            topics,
        };
    }
    // Decode a filter for the event and the search criteria
    decodeEventLog(eventFragment, data, topics) {
        if (typeof eventFragment === 'string') {
            eventFragment = this.getEvent(eventFragment);
        }
        if (topics != null && !eventFragment.anonymous) {
            const topicHash = this.getEventTopic(eventFragment);
            if (!(0, bytes_1.isHexString)(topics[0], 32) || topics[0].toLowerCase() !== topicHash) {
                logger.throwError('fragment/topic mismatch', logger_1.Logger.errors.INVALID_ARGUMENT, {
                    argument: 'topics[0]',
                    expected: topicHash,
                    value: topics[0],
                });
            }
            topics = topics.slice(1);
        }
        const indexed = [];
        const nonIndexed = [];
        const dynamic = [];
        eventFragment.inputs.forEach((param, index) => {
            if (param.indexed) {
                if (param.type === 'string' ||
                    param.type === 'bytes' ||
                    param.baseType === 'tuple' ||
                    param.baseType === 'array') {
                    indexed.push(fragments_js_1.ParamType.fromObject({ type: 'bytes32', name: param.name }));
                    dynamic.push(true);
                }
                else {
                    indexed.push(param);
                    dynamic.push(false);
                }
            }
            else {
                nonIndexed.push(param);
                dynamic.push(false);
            }
        });
        const resultIndexed = topics != null ? this._abiCoder.decode(indexed, (0, bytes_1.concat)(topics)) : null;
        const resultNonIndexed = this._abiCoder.decode(nonIndexed, data, true);
        const result = [];
        let nonIndexedIndex = 0;
        let indexedIndex = 0;
        eventFragment.inputs.forEach((param, index) => {
            if (param.indexed) {
                if (resultIndexed == null) {
                    result[index] = new Indexed({ _isIndexed: true, hash: null });
                }
                else if (dynamic[index]) {
                    result[index] = new Indexed({
                        _isIndexed: true,
                        hash: resultIndexed[indexedIndex++],
                    });
                }
                else {
                    try {
                        result[index] = resultIndexed[indexedIndex++];
                    }
                    catch (error) {
                        result[index] = error;
                    }
                }
            }
            else {
                try {
                    result[index] = resultNonIndexed[nonIndexedIndex++];
                }
                catch (error) {
                    result[index] = error;
                }
            }
            // Add the keyword argument if named and safe
            if (param.name && result[param.name] == null) {
                const value = result[index];
                // Make error named values throw on access
                if (value instanceof Error) {
                    Object.defineProperty(result, param.name, {
                        enumerable: true,
                        get: () => {
                            throw wrapAccessError(`property ${JSON.stringify(param.name)}`, value);
                        },
                    });
                }
                else {
                    result[param.name] = value;
                }
            }
        });
        // Make all error indexed values throw on access
        for (let i = 0; i < result.length; i++) {
            const value = result[i];
            if (value instanceof Error) {
                Object.defineProperty(result, i, {
                    enumerable: true,
                    get: () => {
                        throw wrapAccessError(`index ${i}`, value);
                    },
                });
            }
        }
        return Object.freeze(result);
    }
    // Given a transaction, find the matching function fragment (if any) and
    // determine all its properties and call parameters
    parseTransaction(tx) {
        const fragment = this.getFunction(tx.data.substring(0, 10).toLowerCase());
        if (!fragment) {
            return null;
        }
        return new TransactionDescription({
            args: this._abiCoder.decode(fragment.inputs, `0x${tx.data.substring(10)}`),
            functionFragment: fragment,
            name: fragment.name,
            signature: fragment.format(),
            sighash: this.getSighash(fragment),
            value: bignumber_1.BigNumber.from(tx.value || '0'),
        });
    }
    // @TODO
    // parseCallResult(data: BytesLike): ??
    // Given an event log, find the matching event fragment (if any) and
    // determine all its properties and values
    parseLog(log) {
        const fragment = this.getEvent(log.topics[0]);
        if (!fragment || fragment.anonymous) {
            return null;
        }
        // @TODO: If anonymous, and the only method, and the input count matches, should we parse?
        //        Probably not, because just because it is the only event in the ABI does
        //        not mean we have the full ABI; maybe just a fragment?
        return new LogDescription({
            eventFragment: fragment,
            name: fragment.name,
            signature: fragment.format(),
            topic: this.getEventTopic(fragment),
            args: this.decodeEventLog(fragment, log.data, log.topics),
        });
    }
    parseError(data) {
        const hexData = (0, bytes_1.hexlify)(data);
        const fragment = this.getError(hexData.substring(0, 10).toLowerCase());
        if (!fragment) {
            return null;
        }
        return new ErrorDescription({
            args: this._abiCoder.decode(fragment.inputs, `0x${hexData.substring(10)}`),
            errorFragment: fragment,
            name: fragment.name,
            signature: fragment.format(),
            sighash: this.getSighash(fragment),
        });
    }
    /*
    static from(value: Array<Fragment | string | JsonAbi> | string | Interface) {
        if (Interface.isInterface(value)) {
            return value;
        }
        if (typeof(value) === "string") {
            return new Interface(JSON.parse(value));
        }
        return new Interface(value);
    }
    */
    static isInterface(value) {
        return !!(value && value._isInterface);
    }
}
exports.Interface = Interface;
//# sourceMappingURL=interface.js.map